import { useRef, useState } from 'react'
import { FileUp, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

type Props = {
  busy: boolean
  intoProjectName?: string | null
  onFile: (file: File) => void
}

/** One file, dropped or picked. Winner writes its ASCII export as `.e01`, `.asc` or `.txt`. */
export function WinnerDropZone({ busy, intoProjectName, onFile }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const take = (files: FileList | null) => {
    const file = files?.[0]
    if (file && !busy) onFile(file)
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 py-10 text-center">
      <div className="space-y-2">
        <h3 className="text-2xl font-semibold tracking-tight">
          {intoProjectName ? `Ajouter un devis Winner à « ${intoProjectName} »` : 'Un fichier Winner, un dossier complet'}
        </h3>
        <p className="text-muted-foreground">
          {intoProjectName
            ? 'Soly lit le devis, vérifie chaque ligne et le range dans ce chantier.'
            : 'Soly crée le client, le chantier et le devis — ligne par ligne, au cent près. Vous vérifiez, vous validez.'}
        </p>
      </div>

      <button
        type="button"
        data-testid="winner-import-dropzone-btn"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          take(e.dataTransfer.files)
        }}
        className={cn(
          'flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed px-8 py-14 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2',
          dragging ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50/60'
        )}
      >
        {busy ? (
          <Loader2 className="h-10 w-10 animate-spin text-slate-500" />
        ) : (
          <FileUp className="h-10 w-10 text-slate-500" />
        )}
        <span className="text-base font-medium">Glissez l&apos;export Winner ici</span>
        <span className="text-sm text-muted-foreground">ou cliquez pour le choisir · fichier .e01, .asc ou .txt</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".e01,.asc,.txt,.ascii"
        data-testid="winner-import-file-input"
        onChange={(e) => {
          take(e.target.files)
          e.target.value = ''
        }}
      />

      <p className="text-xs text-muted-foreground">
        Dans Winner : <span className="font-medium">Fichier › Exporter › ASCII</span>. Rien n&apos;est créé avant votre validation.
      </p>
    </div>
  )
}
